import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import type { Position } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconMore } from '@sorare/core/src/atoms/icons/system/iconMore.ts';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { ResponsiveImg } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import type { IntlContext } from '@sorare/core/src/contexts/intl';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { anyPositionShortNames } from '@sorare/core/src/lib/players';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { InjuryTag } from '#src/components/player/InjuryTag/index.tsx';

import type { CommonPlayerMetas_anyPlayer } from './__generated__/index.graphql.ts';

const Cell = styled(Horizontal)`
  padding: var(--unit) 0;
  background-color: var(--current-row-color);
  z-index: 1;
  width: var(--player-metas-width);

  @media ${laptopAndAbove} {
    padding: var(--double-unit) 0;
  }
`;

const PlayerAvatar = styled(ResponsiveImg)`
  flex-shrink: 0;
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  object-fit: contain;
  border-radius: 50%;
  background-color: var(--c-nd-50);

  @media ${laptopAndAbove} {
    border-radius: 0;
    object-fit: cover;
    object-position: top center;
    background-color: transparent;
    width: calc(10 * var(--unit));
    height: calc(10 * var(--unit));
  }
`;

const PlayerMetas = styled(Vertical)`
  flex-grow: 1;
  text-align: left;
`;

const StyledBodyS = styled(BodyS)`
  vertical-align: middle;
`;

const AvatarShadow = styled.div`
  display: none;
  @media ${laptopAndAbove} {
    display: block;
    position: absolute;
    bottom: 0;
    top: 0;
    left: 0;
    background: linear-gradient(
      180deg,
      rgba(16, 16, 16, 0) 55.17%,
      var(--current-row-color) 100%
    );
    width: 100%;
  }
`;
const HorizontalWrap = styled(Horizontal)`
  flex-wrap: wrap;
`;

const Relative = styled.div`
  position: relative;
  isolation: isolate;
`;

const StyledLink = styled(Link)`
  transition: all 0.2s;
  &:hover {
    color: var(--c-blue-300);
  }
`;

const PlayerName = styled(LabelS).attrs({ bold: true, uppercase: true })`
  font-family: var(--brand-font);
  line-height: 1;
  @media ${laptopAndAbove} {
    font-size: var(--fs-16);
  }
`;

const StyledButtonBase = styled(ButtonBase)`
  width: 100%;
`;

const formatPositions = (
  currentPositions: Position[],
  formatMessage: IntlContext['formatMessage'],
  cardPositions?: Position[]
) => {
  const currentPositionsStr = currentPositions
    .map(p => formatMessage(anyPositionShortNames[p]))
    .join(', ');
  const prevPositions = [...new Set(cardPositions)].filter(
    p => !currentPositions.includes(p)
  );
  if (prevPositions && prevPositions.length > 0) {
    const prevPositionsStr = prevPositions
      .map(p => formatMessage(anyPositionShortNames[p]))
      .join(', ');

    return (
      <FormattedMessage
        defaultMessage="{currentPositionsStr} (prev. {prevPositionsStr})"
        values={{
          currentPositionsStr,
          prevPositionsStr,
        }}
      />
    );
  }
  return currentPositionsStr;
};

type Props = {
  anyPlayer: CommonPlayerMetas_anyPlayer;
  onStatsClick?: () => void;
};

export const CommonPlayerMetas = ({ anyPlayer, onStatsClick }: Props) => {
  const { formatMessage } = useIntlContext();
  const { up: isLaptopOrAbove } = useScreenSize('laptop');
  const { generateSportContextPath } = useSportContext();
  const to = generateSportContextPath(ANY_SPORT_PLAYERS_SLUG, {
    params: { slug: anyPlayer.slug },
  });
  return (
    <ConditionalWrapper
      wrap={!isLaptopOrAbove}
      Wrapper={(props: { children: ReactNode }) => (
        <LinkBox>
          <LinkOverlay
            aria-label={anyPlayer.displayName}
            as={StyledButtonBase}
            onClick={onStatsClick}
          />
          {props.children}
        </LinkBox>
      )}
    >
      <Cell gap={isLaptopOrAbove ? 2 : 1}>
        <Relative>
          <ConditionalWrapper
            wrap={isLaptopOrAbove}
            Wrapper={(p: { children: ReactNode }) => (
              <Link to={to}>{p.children}</Link>
            )}
          >
            <PlayerAvatar
              cropWidth={80}
              src={
                isLaptopOrAbove
                  ? anyPlayer?.fullPictureUrl
                  : anyPlayer?.avatarPictureUrl
              }
              alt=""
            />
            <AvatarShadow />
          </ConditionalWrapper>
        </Relative>
        <PlayerMetas gap={0.5}>
          <ConditionalWrapper wrap={!isLaptopOrAbove} Wrapper={Ellipsis}>
            <ConditionalWrapper
              wrap={isLaptopOrAbove}
              Wrapper={(p: { children: ReactNode }) => (
                <StyledLink to={to}>{p.children}</StyledLink>
              )}
            >
              <PlayerName>
                {isLaptopOrAbove
                  ? anyPlayer.displayName
                  : anyPlayer.abbreviatedName}
              </PlayerName>
            </ConditionalWrapper>
          </ConditionalWrapper>
          <HorizontalWrap gap={1}>
            <StyledBodyS color="var(--c-nd-600)">
              {formatPositions(
                anyPlayer.anyPositions,
                formatMessage,
                anyPlayer.cardPositions
              )}{' '}
              •{' '}
              <FormattedMessage
                defaultMessage="{age} yo"
                values={{ age: anyPlayer.age }}
              />
            </StyledBodyS>
            <InjuryTag player={anyPlayer} size="small" />
          </HorizontalWrap>
        </PlayerMetas>
        {!isLaptopOrAbove && (
          <LinkOther
            as={IconButton}
            size="compact"
            icon={iconMore}
            color="quaternary"
            onClick={onStatsClick}
          />
        )}
      </Cell>
    </ConditionalWrapper>
  );
};

CommonPlayerMetas.fragments = {
  anyPlayer: gql`
    fragment CommonPlayerMetas_anyPlayer on AnyPlayerInterface {
      slug
      currentUserSubscription {
        slug
      }
      displayName
      abbreviatedName
      sport
      anyPositions
      cardPositions
      fullPictureUrl
      avatarPictureUrl
      age
      ...InjuryTag_anyPlayer
    }
    ${InjuryTag.fragments.anyPlayer}
  ` as TypedDocumentNode<CommonPlayerMetas_anyPlayer>,
};
